/**
 * The tasks an assignment asks for, and what each one comes to against criterion C1.
 *
 * 🔴 A MIRROR OF `api/turbine/domain/tasks.py`, AND THE SERVER IS THE AUTHORITY. It exists so the
 * Brief tab can show each unit's points while a teacher types its weight. The server scales the
 * weights again on every run and refuses on its own; if the two ever disagree, the server wins.
 * 🔴 SO THEY ARE GATED AGAINST EACH OTHER: `src/tasks.vectors.json` is generated from the Python
 * by `sync-rubric-vectors.sh` and asserted by vitest here and by pytest there.
 * 🔴 A UNIT IS WHAT HOLDS REQUIREMENTS AND A WEIGHT: a subtask, or a task with no subtasks. The
 * required units' weights are scaled to C1's points by largest remainder, ties to the lower id;
 * an optional unit gets its weight's share, rounded down. Integer arithmetic throughout, so a tie
 * is a tie on every machine.
 */
import type { SubtaskDraft, TaskDraft } from "./api";

export type { SubtaskDraft, TaskDraft };

export type TaskCounts = "required" | "extra_credit" | "report_only";

/** A task with the ids the server will give it and its subtasks. */
export type IdTask = Omit<TaskDraft, "subtasks"> & {
  id: string; subtasks: (SubtaskDraft & { id: string })[];
};

const taskNumber = (id: string): number => {
  const m = /^T([1-9][0-9]{0,2})$/.exec(id);
  return m ? Number(m[1]) : 0;
};
/** A subtask's own number — only ever read over one task's stored subtasks, all minted under it. */
const subNumber = (id: string): number => {
  const m = /^T([1-9][0-9]{0,2})\.([1-9][0-9]?)$/.exec(id);
  return m ? Number(m[2]) : 0;
};
/** `T2` before `T10`, `T1.2` before `T1.10`: the numbers, not the text. */
const idKey = (id: string): [number, number, string] => {
  const m = /^T([1-9][0-9]{0,2})\.([1-9][0-9]?)$/.exec(id);
  return m ? [Number(m[1]), Number(m[2]), id] : [taskNumber(id), 0, id];
};
const byKey = (a: string, b: string): number => {
  const x = idKey(a), y = idKey(b);
  return (x[0] - y[0]) || (x[1] - y[1]) || x[2].localeCompare(y[2]);
};

/** A task's subtasks, or none — a task stored before subtasks existed has the key absent. */
export const subtasksOf = (t: TaskDraft): SubtaskDraft[] => t.subtasks ?? [];

/** The ids the server will give these tasks and their subtasks: a stored id is kept, anything
 *  else gets the next number after the highest the stored ones use — `tasks.normalise`'s rule. */
export function withIds(draft: readonly TaskDraft[], saved: readonly TaskDraft[]): IdTask[] {
  const stored = new Map(saved.filter((t) => t.id).map((t) => [t.id ?? "", t]));
  let top = saved.reduce((n, t) => Math.max(n, taskNumber(t.id ?? "")), 0);
  const used = new Set<string>();
  return draft.map((t) => {
    const given = t.id ?? "";
    let id: string;
    if (given && stored.has(given) && !used.has(given)) id = given;
    else { top += 1; id = `T${top}`; }
    used.add(id);
    const was = stored.get(id);
    const prev = was ? subtasksOf(was) : [];
    const known = new Set(prev.map((s) => s.id ?? ""));
    let subTop = prev.reduce((n, s) => Math.max(n, subNumber(s.id ?? "")), 0);
    const subUsed = new Set<string>();
    const subtasks = subtasksOf(t).map((s) => {
      const g = s.id ?? "";
      let sid: string;
      if (g && known.has(g) && !subUsed.has(g)) sid = g;
      else { subTop += 1; sid = `${id}.${subTop}`; }
      subUsed.add(sid);
      return { ...s, id: sid };
    });
    return { ...t, id, subtasks };
  });
}

/** Whole points summing to exactly `target`, by largest remainder; ties go to the lower id. */
export function split(target: number, rows: readonly { id: string; weight: number }[]): number[] {
  const total = rows.reduce((n, r) => n + r.weight, 0);
  if (total <= 0 || target <= 0) return rows.map(() => 0);
  const parts = rows.map((r, i) => {
    const num = r.weight * target;
    return { i, id: r.id, floor: Math.floor(num / total), rem: num % total };
  });
  const order = [...parts].sort((a, b) => (b.rem - a.rem) || byKey(a.id, b.id));
  let short = target - parts.reduce((n, p) => n + p.floor, 0);
  for (const p of order) {
    if (short <= 0) break;
    p.floor += 1;
    short -= 1;
  }
  return parts.map((p) => p.floor);
}

/** What holds a task's requirements and weight: its subtasks, or the task itself. */
export function unitsOf(t: IdTask): { id: string; weight: number; requirements: string[] }[] {
  return t.subtasks.length
    ? t.subtasks.map((s) => ({ id: s.id, weight: s.weight || 0, requirements: s.requirements }))
    : [{ id: t.id, weight: t.weight || 0, requirements: t.requirements }];
}

export interface UnitPoints { id: string; task: string; counts: TaskCounts; points: number }

/** What each unit comes to against `total` points of C1, each task's total, and the units that
 *  come to 0 — the configuration the server refuses. `tasks.attach` in Python. */
export function taskPoints(tasks: readonly IdTask[], total: number):
  { units: UnitPoints[]; byTask: Map<string, number>; zero: string[]; requiredWeight: number } {
  const req = tasks.filter((t) => !t.optional).flatMap(unitsOf);
  const requiredWeight = req.reduce((n, u) => n + u.weight, 0);
  const pts = split(total, req);
  const byId = new Map(req.map((u, i) => [u.id, pts[i] ?? 0]));
  const units: UnitPoints[] = [];
  const byTask = new Map<string, number>();
  for (const t of tasks) {
    const counts: TaskCounts = !t.optional ? "required"
      : t.extra_credit ? "extra_credit" : "report_only";
    let sum = 0;
    for (const u of unitsOf(t)) {
      const points = !t.optional ? (byId.get(u.id) ?? 0)
        : requiredWeight > 0 ? Math.floor((u.weight * total) / requiredWeight) : 0;
      units.push({ id: u.id, task: t.id, counts, points });
      sum += points;
    }
    byTask.set(t.id, sum);
  }
  // ⚠️ AN EXTRA-CREDIT UNIT AT 0 IS REFUSED TOO: it could never make up a point.
  const zero = units.filter((u) => u.counts !== "report_only" && u.points === 0).map((u) => u.id);
  return { units, byTask, zero, requiredWeight };
}

const oneLine = (s: string) => s.split(/\s+/).filter(Boolean).join(" ");
const lines = (rs: readonly string[]) => rs.map(oneLine).filter(Boolean);

/** A task as the server will store it: names one line, requirements without blank lines, and
 *  either subtasks (with the weights) or requirements and a weight of its own. */
export function tidyTask(t: TaskDraft): TaskDraft {
  const subs = subtasksOf(t);
  const base: TaskDraft = {
    ...(t.id ? { id: t.id } : {}),
    title: oneLine(t.title), description: (t.description ?? "").trim(),
    optional: t.optional, extra_credit: t.optional && t.extra_credit,
    requirements: subs.length ? [] : lines(t.requirements),
    subtasks: subs.map((s) => ({
      ...(s.id ? { id: s.id } : {}),
      title: oneLine(s.title), description: (s.description ?? "").trim(),
      requirements: lines(s.requirements), weight: Math.round(Number(s.weight) || 0) })),
  };
  return subs.length ? base : { ...base, weight: Math.round(Number(t.weight) || 0) };
}

/** The default a proposal starts from: required units split `points` evenly, ties to the lower
 *  id, and each optional unit weighed like one required unit. `tasks.even` in Python. */
export function evenWeights(tasks: readonly IdTask[], points: number): IdTask[] {
  const req = tasks.filter((t) => !t.optional).flatMap(unitsOf);
  if (!req.length) return tasks.map((t) => ({ ...t }));
  const share = split(points, req.map((u) => ({ id: u.id, weight: 1 })));
  const by = new Map(req.map((u, i) => [u.id, share[i] ?? 0]));
  const each = Math.max(1, Math.floor(points / req.length));
  const w = (id: string) => Math.max(1, by.get(id) ?? each);
  return tasks.map((t) => (t.subtasks.length
    ? { ...t, subtasks: t.subtasks.map((s) => ({ ...s, weight: w(s.id) })) }
    : { ...t, weight: w(t.id) }));
}
